import React from "react";
import ChatLearningSpace from "../ChatLearningSpace";
import { saveMode, MODES, ChatMode } from "./chatConfig";

/**
 * ✅ EASY SWITCH LATER:
 * - Keep all modes + toggles logic in this file
 * - Just flip ENABLE_MODE_UI to true when you want the header back
 */
const ENABLE_MODE_UI = false;

// ✅ Default: all OpenAI method (OpenAI chat + "free" voice = your OpenAI TTS)
const DEFAULT_AI: "openai" | "ollama" = "openai";
const DEFAULT_VOICE: "eleven" | "browser" = "browser";

function modeFrom(ai: "openai" | "ollama", voice: "eleven" | "browser"): ChatMode {
  const key =
    ai === "openai" && voice === "eleven"
      ? "openai-eleven"
      : ai === "openai" && voice === "browser"
      ? "openai-browser"
      : ai === "ollama" && voice === "eleven"
      ? "ollama-eleven"
      : "ollama-browser";

  return MODES.find((m) => m.key === key) || MODES[0];
}

function SegmentedToggle({
  value,
  options,
  onChange,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div
      style={{
        display: "inline-flex",
        gap: 4,
        padding: 4,
        borderRadius: 999,
        border: "1px solid rgba(20,20,40,0.12)",
        background: "rgba(255,255,255,0.7)",
        backdropFilter: "blur(10px)",
      }}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            style={{
              border: "none",
              borderRadius: 999,
              padding: "8px 12px",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              background: active ? "rgba(123, 97, 255, 0.95)" : "transparent",
              color: active ? "white" : "rgba(20,20,40,0.85)",
              boxShadow: active ? "0 8px 18px rgba(123,97,255,0.22)" : "none",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export default function ChatPage() {
  // ✅ Force defaults (no loading from storage)
  const [ai, setAi] = React.useState<"openai" | "ollama">(DEFAULT_AI);
  const [voice, setVoice] = React.useState<"eleven" | "browser">(DEFAULT_VOICE);

  const activeMode = React.useMemo(() => modeFrom(ai, voice), [ai, voice]);

  // ✅ Still persist the chosen mode (so later UI restore works instantly)
  React.useEffect(() => {
    saveMode(activeMode);
  }, [activeMode]);

  return (
    <div style={{ height: "100vh", overflow: "hidden" }}>
      {/* Optional header UI (currently disabled) */}
      {ENABLE_MODE_UI ? (
        <div
          style={{
            height: 56,
            display: "flex",
            alignItems: "center",
            padding: "0 14px",
            gap: 10,
            borderBottom: "1px solid rgba(20,20,40,0.10)",
            background: "rgba(255,255,255,0.82)",
            backdropFilter: "blur(12px)",
          }}
        >
          <div style={{ fontWeight: 800, fontSize: 14, color: "rgba(20,20,40,0.9)" }}>
            Bella
          </div>

          <SegmentedToggle
            value={ai}
            onChange={(v) => setAi(v as any)}
            options={[
              { value: "openai", label: "Online" },
              { value: "ollama", label: "Lokaal" },
            ]}
          />

          <SegmentedToggle
            value={voice}
            onChange={(v) => setVoice(v as any)}
            options={[
              { value: "browser", label: "Gratis stem" },
              { value: "eleven", label: "Premium stem" },
            ]}
          />
        </div>
      ) : null}

      {/* Chat takes full height (no top banner) */}
      <div style={{ height: "100%", overflow: "hidden" }}>
        <ChatLearningSpace
          aiProvider={ai}
          voiceProvider={voice}
          openaiUrl={activeMode.openaiUrl}
          ollamaUrl={activeMode.ollamaUrl}
        />
      </div>
    </div>
  );
}
